import React, { useEffect, useState } from 'react';
import Header from '../../layouts/Header';
import axios from '../../api/axios';
import { format } from 'date-fns';
import {
  Card,
  Table,
  Badge,
  Form,
  Row,
  Col,
  Button,
  Accordion,
  Spinner,
  Nav,
  Tab,
} from 'react-bootstrap';
import { FunnelFill, ArrowClockwise, ExclamationTriangleFill, ListCheck, BoxSeam } from 'react-bootstrap-icons';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from 'react-toastify';

const Logs = () => {
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [expandedLog, setExpandedLog] = useState(null);
  const [activeTab, setActiveTab] = useState('activity'); // 'error', 'activity', or 'product'

  const accessToken = localStorage.getItem('authToken');
  const [errorLogsData, setErrorLogsData] = useState([]);
  const [activityLogsData, setActivityLogsData] = useState([]);
  const [productLogsData, setProductLogsData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [searchBtnLoading, setSearchBtnLoading] = useState(false);
  const [ifSearchBtn, setIfSearchBtn] = useState(false);
  const [touched, setTouched] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState('UPDATE-SINGLE-MENU-PRODUCT-PRICING');

  // Filter states
  const [filters, setFilters] = useState({
    originalUrl: '',
    message: '',
    fromDate: null,
    toDate: null,
    errorType: ''
  });

  // Fetch error logs
  const getErrorLogs = async (startD, endD) => {
    try {
      setLoading(true);
      const headers = {
        Authorization: `Bearer ${accessToken}`
      };

      let url = `errorlog?fromDate=${startD.toISOString().split("T")[0]}&toDate=${endD.toISOString().split("T")[0]}`;
      
      if (filters.message) {
        url += `&originalUrl=${filters.message}`;
      }
      if (filters.errorType) {
        url += `&errorType=${filters.errorType}`;
      }

      const response = await axios.get(url, { headers });
      setErrorLogsData(response?.data?.data?.errorLog || []);
      if (activeTab === 'error') {
        toast.success(response?.data?.data?.message, {
          position: "top-right",
          autoClose: 3000,
          theme: "light",
        });
      }
    } catch (error) {
      console.error("Error fetching error logs:", error);
      let errorMessage = error?.response?.data?.error || "Failed to fetch error logs";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000,
        theme: "light",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch activity logs
  const getActivityLogs = async (startD, endD, searchT) => {
    try {
      if(ifSearchBtn) {
        setLoading(false);
      } else {
        setLoading(true);
      }
      const headers = {
        Authorization: `Bearer ${accessToken}`
      };

      let newUrl = `getAllData/activityLogs?startDate=${startD.toISOString().split("T")[0]}&endDate=${endD.toISOString().split("T")[0]}`

      if(searchT) {
        newUrl += `&search=${searchT}`
      }

      const response = await axios.get(newUrl, { headers });
      console.log("Response of Activity Logs:", response);

      setActivityLogsData(response?.data?.data || []);
      if (activeTab === 'activity') {
        toast.success("Activity logs fetched successfully", {
          position: "top-right",
          autoClose: 3000,
          theme: "light",
        });  
      }
      
    } catch (error) {
      console.error("Error fetching activity logs:", error);
      let errorMessage = error?.response?.data?.error || "Failed to fetch activity logs";
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000,
        theme: "light",
      });
    } finally {
      setLoading(false);
      setSearchBtnLoading(false)
    }
  };

  // Fetch product logs
  const getProductLogs = async (startD, endD, sEvent) => {
    try {
      setLoading(true);
      const headers = {
        Authorization: `Bearer ${accessToken}`
      };

      let url = `product/getProductLogs?startDate=${startD.toISOString().split("T")[0]}&endDate=${endD.toISOString().split("T")[0]}`;

      if (sEvent) {
        url += `&event=${sEvent}`
      }
      const response = await axios.get(url, { headers });
      console.log("Response of Product Logs:", response);

      setProductLogsData(response?.data?.data?.data || []);
      if (activeTab === 'product') {
        toast.success("Product logs fetched successfully", {
          position: "top-right",
          autoClose: 3000,
          theme: "light",
        });
      }
    } catch (error) {
      console.error("Error fetching product logs:", error);
      let errorMessage = error?.response?.data?.error || "Failed to fetch product logs";
      if (activeTab === 'product') {
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 3000,
          theme: "light",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle date changes
  const handleDateChange = (date, field) => {
    setFilters(prev => ({
      ...prev,
      [field]: date
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setTouched(true);
  };

  const handleSubmitSearch = () => {
    setSearchBtnLoading(true);
    setIfSearchBtn(true);
    getActivityLogs(filters.fromDate, filters.toDate, searchText);
  }

  const resetFilters = () => {
    setFilters({
      originalUrl: '',
      message: '',
      errorType: ''
    });
  };  

  const toggleExpandLog = (id) => {
    setExpandedLog(expandedLog === id ? null : id);
  };

  const handleTabSelect = (tab) => {
    setActiveTab(tab);
  };

  const fetchLogs = () => {
    switch (activeTab) {
      case 'error':
        getErrorLogs(filters.fromDate, filters.toDate);
        break;
      case 'activity':
        getActivityLogs(filters.fromDate, filters.toDate, searchText);
        break;
      case 'product':
        getProductLogs(filters.fromDate, filters.toDate, selectedEvent);
        break;
      default:
        getActivityLogs(filters.fromDate, filters.toDate, searchText);
    }
  };

  useEffect(() => {
    const now = new Date();
    const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const start = new Date(currentDate.setHours(23, 59, 59, 999));
    const end = new Date(currentDate.setHours(23, 59, 59, 999));
    
    // Initialize all logs
    getActivityLogs(start, end, searchText);
    getErrorLogs(start, end);
    getProductLogs(start, end);
  }, []);

  useEffect(() => {
    const now = new Date();
    const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const start = new Date(currentDate.setHours(23, 59, 59, 999));
    const end = new Date(currentDate.setHours(23, 59, 59, 999));

    setFilters(prev => ({
      ...prev,
      fromDate: start,
      toDate: end
    }));
  }, []);

  useEffect(() => {
    const now = new Date();
    const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const start = new Date(currentDate.setHours(23, 59, 59, 999));
    const end = new Date(currentDate.setHours(23, 59, 59, 999));

    if (searchText === "" && touched) {
      getActivityLogs(start, end, searchText);
    }
  }, [searchText]);

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className={`main main-app p-3 p-lg-4 ${skin === 'dark' ? 'bg-dark' : 'bg-light'}`}>
        <Card className={`mb-4 ${skin === 'dark' ? 'bg-dark text-white' : ''}`}>
          <Card.Header className="d-flex justify-content-between align-items-center bg-white">
            <h5 className="mb-0">
              {activeTab === 'error' ? (
                <ExclamationTriangleFill className="me-2" />
              ) : activeTab === 'product' ? (
                <BoxSeam className="me-2" />
              ) : (
                <ListCheck className="me-2" />
              )}
              {activeTab === 'error' ? 'Error Logs' : activeTab === 'product' ? 'Product Logs' : 'Activity Logs'}
            </h5>
            <div>
              <Button
                variant={skin === 'dark' ? 'outline-light' : 'outline-secondary'}
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="me-2"
              >
                <FunnelFill className="me-1" />
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
              <Button
                variant={skin === 'dark' ? 'outline-light' : 'outline-primary'}
                size="sm"
                onClick={fetchLogs}
                disabled={loading}
              >
                <ArrowClockwise className={`me-1 ${loading ? 'spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </Card.Header>

          {showFilters && (
            <Card.Body className={`${skin === 'dark' ? 'bg-dark' : 'bg-white'}`}>
              <Form>
                <Row>
                  <Col md={2}>
                    <Form.Group controlId="fromDate" className='d-flex flex-column'>
                      <Form.Label className='fw-semibold'>From Date</Form.Label>
                      <DatePicker
                        selected={filters?.fromDate}
                        onChange={(date) => handleDateChange(date, 'fromDate')}
                        selectsStart
                        startDate={filters?.fromDate}
                        endDate={filters?.toDate}
                        maxDate={filters?.toDate || new Date()}
                        dateFormat="dd-MM-yyyy"
                        placeholderText="Start Date"
                        className="form-control"
                        wrapperClassName="w-100"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group controlId="toDate" className='d-flex flex-column'>
                      <Form.Label className='fw-semibold'>To Date</Form.Label>
                      <DatePicker
                        selected={filters?.toDate}
                        onChange={(date) => handleDateChange(date, 'toDate')}
                        selectsEnd
                        startDate={filters?.fromDate}
                        endDate={filters?.toDate}
                        minDate={filters?.fromDate}
                        maxDate={new Date()}
                        dateFormat="dd-MM-yyyy"
                        placeholderText="End Date"
                        className="form-control"
                        wrapperClassName="w-100"
                      />
                    </Form.Group>
                  </Col>
                  {activeTab === 'product' && (
                    <Col md={4}>
                      <Form.Group controlId="event" className='d-flex flex-column'>
                        <Form.Label className='fw-semibold'>Select Event</Form.Label>
                        <Form.Select onChange={(e) => setSelectedEvent(e.target.value)} value={selectedEvent}>
                          <option value="">ALL</option>
                          <option value="PRODUCT-ADD">PRODUCT-ADD</option>
                          <option value="PRODUCT-UPDATE">PRODUCT-UPDATE</option>
                          <option value="PRODUCT-DELETE">PRODUCT-DELETE</option>
                          <option value="PRODUCT-EXHAUSTED">PRODUCT-EXHAUSTED</option>
                          <option value="UPDATE-SINGLE-MENU-PRODUCT-PRICING">UPDATE-SINGLE-MENU-PRODUCT-PRICING</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  )}
                  {activeTab === 'activity' && (
                    <Col md={4}>
                      <Form.Label className='fw-semibold'>Search</Form.Label>
                      <div className="input-group w-100">
                        <input
                            type="text"
                            className="form-control"
                            value={searchText}
                            onChange={handleSearchChange}
                            placeholder="Search for logs..."
                        />
                        <button className="btn btn-primary" type="button" onClick={handleSubmitSearch} disabled={!searchText}>
                            <i className="ri-search-line"></i> { searchBtnLoading ? <Spinner size='sm' animation='border' /> : 'Search' }
                        </button>
                      </div>
                    </Col>
                  )}
                  {activeTab === 'error' && (
                    <Col md={2}>
                      <Form.Group controlId="errorType">
                        <Form.Label className='fw-semibold'>Error Type</Form.Label>
                        <Form.Select
                          as="select"
                          name="errorType"
                          value={filters?.errorType}
                          onChange={handleFilterChange}
                          className={skin === 'dark' ? 'bg-dark text-white' : ''}
                        >
                          <option value="">All Types</option>
                          <option value="Error">Error</option>
                          <option value="TypeError">TypeError</option>
                          <option value="ReferenceError">ReferenceError</option>
                          <option value="SyntaxError">SyntaxError</option>
                          <option value="RangeError">RangeError</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  )}
                  <Col className='d-flex align-items-end'>
                    <Button
                      variant="primary"
                      onClick={fetchLogs}
                      disabled={loading}
                      className="me-2"
                    >
                      {loading ? <Spinner size='sm'/> : 'Apply Filters'}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      onClick={resetFilters}
                    >
                      Reset
                    </Button>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          )}

          <Card.Body className={`p-0 ${skin === 'dark' ? 'bg-dark' : 'bg-white'}`}>
            <Tab.Container activeKey={activeTab} onSelect={handleTabSelect}>
              <Nav variant="tabs" className="px-3 pt-3">
                <Nav.Item>
                  <Nav.Link eventKey="activity">
                    <ListCheck className="me-1" />
                    Activity Logs
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="product">
                    <BoxSeam className="me-1" />
                    Product Logs
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="error">
                    <ExclamationTriangleFill className="me-1" />
                    Error Logs
                  </Nav.Link>
                </Nav.Item>
              </Nav>
              
              <Tab.Content>
                <Tab.Pane eventKey="activity">
                  <ActivityLogsTable 
                    logsData={activityLogsData} 
                    loading={loading}
                    skin={skin}
                  />
                </Tab.Pane>
                <Tab.Pane eventKey="product">
                  <ProductLogsTable 
                    logsData={productLogsData} 
                    loading={loading}
                    skin={skin}
                  />
                </Tab.Pane>
                <Tab.Pane eventKey="error">
                  <ErrorLogsTable 
                    logsData={errorLogsData} 
                    loading={loading} 
                    skin={skin}
                    expandedLog={expandedLog}
                    toggleExpandLog={toggleExpandLog}
                  />
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </Card.Body>
          
          <Card.Footer className={`text-muted ${skin === 'dark' ? 'bg-dark' : 'bg-light'}`}>
            Showing {
              activeTab === 'error' ? errorLogsData?.length : 
              activeTab === 'product' ? productLogsData?.length : 
              activityLogsData?.length
            } logs
          </Card.Footer>
        </Card>
      </div>
    </React.Fragment>
  );
};

// Error Logs Table
const ErrorLogsTable = ({ logsData, loading, skin, expandedLog, toggleExpandLog }) => {
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading error logs...</p>
      </div>
    );
  }

  if (logsData.length === 0) {
    return (
      <div className="text-center py-5">
        <p className='text-danger fw-semibold'>No error logs found for the selected criteria</p>
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <Table striped bordered hover variant={skin === 'dark' ? 'dark' : ''} className="mb-0">
        <thead>
          <tr>
            <th style={{ width: '15%' }}>Timestamp</th>
            <th style={{ width: '25%' }}>Endpoint</th>
            <th style={{ width: '20%' }}>Error Type</th>
            <th style={{ width: '30%' }}>Message</th>
            <th style={{ width: '10%' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {logsData.map((log) => (
            <React.Fragment key={log._id}>
              <tr>
                <td>{format(new Date(log?.createdAt), 'MMM dd, yyyy, HH:mm')}</td>
                <td className="text-truncate" style={{ maxWidth: '300px' }}>
                  {log.originalUrl}
                </td>
                <td>
                  <Badge bg="danger">{log.response?.name || 'Unknown'}</Badge>
                </td>
                <td className="text-truncate" style={{ maxWidth: '300px' }}>
                  {log.message}
                </td>
                <td className="text-center">
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => toggleExpandLog(log._id)}
                  >
                    {expandedLog === log._id ? 'Hide' : 'Details'}
                  </Button>
                </td>
              </tr>
              {expandedLog === log._id && (
                <tr>
                  <td colSpan="5" className="p-0">
                    <Accordion activeKey={expandedLog === log._id ? '0' : null}>
                      <Accordion.Collapse eventKey="0">
                        <div className="p-3 bg-secondary bg-opacity-10">
                          <h6>Error Details:</h6>
                          <pre className="p-3 bg-dark text-white rounded">
                            {log.response?.stack || 'No stack trace available'}
                          </pre>
                          <div className="mt-2">
                            <strong>Full Message:</strong>
                            <div className="p-2 bg-light rounded">{log.message}</div>
                          </div>
                        </div>
                      </Accordion.Collapse>
                    </Accordion>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

// Activity Logs Table
const ActivityLogsTable = ({ logsData, loading, skin }) => {
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading activity logs...</p>
      </div>
    );
  }

  if (!logsData || logsData.length === 0) {
    return (
      <div className="text-center py-5">
        <p className='text-info fw-semibold'>No activity logs found for the selected criteria</p>
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <Table striped bordered hover variant={skin === 'dark' ? 'dark' : ''} className="mb-0">
        <thead>
          <tr>
            <th style={{ width: '15%' }}>Timestamp</th>
            <th style={{ width: '20%' }}>User Name</th>
            <th style={{ width: '25%' }}>Email</th>
            <th style={{ width: '20%' }}>Event</th>
            <th style={{ width: '20%' }}>API Endpoint</th>
          </tr>
        </thead>
        <tbody>
          {logsData.map((log) => {
            const userInfo = log.adminUser || log.user || log.outletUser || {};
            return (
              <tr key={log._id}>
                <td>{format(new Date(log.createdAt), 'MMM dd, yyyy, HH:mm')}</td>
                <td>{userInfo.name || 'N/A'}</td>
                <td>{userInfo.email || 'N/A'}</td>
                <td>
                  <Badge bg="info">{log.event}</Badge>
                </td>
                <td>{log.request_api}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

// Product Logs Table - Updated to match your API response
const ProductLogsTable = ({ logsData, loading, skin }) => {
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Loading product logs...</p>
      </div>
    );
  }

  if (!logsData || logsData.length === 0) {
    return (
      <div className="text-center py-5">
        <p className='text-warning fw-semibold'>No product logs found for the selected criteria</p>
      </div>
    );
  }

  // Helper function to get user information
  const getUserInfo = (log) => {
    if (log.adminUser) {
      return {
        name: log.adminUser.name,
        email: log.adminUser.email,
        type: 'Admin'
      };
    } else if (log.user) {
      return {
        name: log.user.name,
        email: log.user.email,
        type: 'User'
      };
    } else if (log.outletUser) {
      return {
        name: log.outletUser.name,
        email: log.outletUser.email,
        type: 'Outlet User'
      };
    }
    return {
      name: 'System',
      email: 'N/A',
      type: 'System'
    };
  };

  // Helper function to format field changes
  // const renderFieldChanges = (updatedFields) => {
  //   if (!updatedFields || updatedFields.length === 0) {
  //     return 'No field changes';
  //   }

  //   return updatedFields.map((field, index) => (
  //     <div key={field._id || index} className="mb-1">
  //       <small>
  //         <strong>{field.field}:</strong> {field.oldValue} → {field.newValue}
  //       </small>
  //     </div>
  //   ));
  // };

  // Helper to convert camelCase to readable words
  const formatFieldName = (name) =>
    name.replace(/([A-Z])/g, ' $1').toLowerCase();

  // Helper function to format field changes
  const renderFieldChanges = (updatedFields) => {
    if (!updatedFields || updatedFields.length === 0) {
      return 'No field changes';
    }

    return updatedFields.map((field, index) => (
      <div key={field._id || index} className="mb-1">
        <small>
          <strong>{formatFieldName(field.field)}:</strong>{' '}
          <span style={{ color: 'red' }}>{field.oldValue}</span> →{' '}
          <span style={{ color: 'green' }}>{field.newValue}</span>
        </small>
      </div>
    ));
  };

  return (
    <div className="table-responsive">
      <Table striped bordered hover variant={skin === 'dark' ? 'dark' : ''} className="mb-0">
        <thead>
          <tr>
            <th >Timestamp</th>
            <th >User</th>
            <th >User Type</th>
            <th >Event</th>
            <th >API Endpoint</th>
            <th >Field Changes</th>
          </tr>
        </thead>
        <tbody>
          {logsData.map((log) => {
            const userInfo = getUserInfo(log);
            
            return (
              <tr key={log._id}>
                <td>
                  {format(new Date(log.createdAt), 'MMM dd, yyyy')}
                  <br />
                  <small className="text-muted">
                    {format(new Date(log.createdAt), 'HH:mm:ss')}
                  </small>
                </td>
                <td>
                  <div>
                    <strong>{userInfo.name}</strong>
                    <br />
                    <small className="text-muted">{userInfo.email}</small>
                  </div>
                </td>
                <td>
                  <Badge bg={
                    userInfo.type === 'Admin' ? 'primary' :
                    userInfo.type === 'User' ? 'info' :
                    userInfo.type === 'Outlet User' ? 'warning' : 'secondary'
                  }>
                    {userInfo.type}
                  </Badge>
                </td>
                <td>
                  <Badge bg={
                    log.event?.includes('UPDATE') ? 'warning' :
                    log.event?.includes('CREATE') ? 'success' :
                    log.event?.includes('DELETE') ? 'danger' : 'info'
                  }>
                    {log.event || 'N/A'}
                  </Badge>
                </td>
                <td>
                  <div className="text-truncate" style={{ maxWidth: '200px' }} title={log.request_api}>
                    {log.request_api}
                  </div>
                  <small className="text-muted">
                    Method: {log.request_method}
                  </small>
                </td>
                <td>
                  {log.updated_fields && log.updated_fields.length > 0 ? (
                    <div style={{ fontSize: '11px' }}>
                      {(() => {
                        const menuGroup = log?.menuGroupDetails?.groupName;
                        const product = log?.productDetails?.name;
                        const changes = log.updated_fields
                          .map(
                            (f) =>
                              `${formatFieldName(f.field)} changed from ${f.oldValue} to ${f.newValue}`
                          )
                          .join(', ');

                        if (menuGroup && product)
                          return (
                            <div>
                              In the <strong>{menuGroup}</strong>, {changes} for{' '}
                              <strong>{product}</strong>.
                            </div>
                          );
                        else if (product)
                          return (
                            <div>
                              <strong>{changes}</strong> for <strong>{product}</strong>.
                            </div>
                          );
                        else return <div>{changes}.</div>;
                      })()}
                      <hr className='mt-0 mb-0' />
                      <Badge bg="success" text="light" className="mt-1">
                        {log.updated_fields.length} field(s) changed
                      </Badge>
                      <hr className='mt-1 mb-0' />
                      <div className="mt-1">{renderFieldChanges(log.updated_fields)}</div>
                    </div>
                  ) : (
                    <span className="text-muted">No changes</span>
                  )}
                </td>
                {/* <td>
                  {log.updated_fields && log.updated_fields.length > 0 ? (
                    <div>
                      {log?.menuGroupDetails && (
                        <div style={{ fontSize: '10px' }}>Menu Group : {log?.menuGroupDetails?.groupName}</div>
                      )}
                      {log?.productDetails && (
                        <div style={{ fontSize: '10px' }}>Product Details : {log?.productDetails?.name}</div>
                      )}
                      <Badge bg="light" text="dark" className="mb-1">
                        {log.updated_fields.length} field(s) changed
                      </Badge>
                      <div className="mt-1">
                        {renderFieldChanges(log.updated_fields)}
                      </div>
                    </div>
                  ) : (
                    <span className="text-muted">No changes</span>
                  )}
                </td> */}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default Logs;